import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import {
  BNtoFormattedString,
  ETH_CHAIN_ID_NUMBER,
  MprolabContractName,
  TokenBNtoNumber,
  TokenBNtoString,
  blockInvalidFloat,
  getChainNameByChainId,
  getNativeCoinTickerByChainId,
  isAddress,
  stableCoinDecimals,
  stringToBigNumber
} from '@mprolab/sdk'
import {
  Button,
  Checkbox,
  Flex,
  Input,
  LayerZeroLogo,
  MPROToken,
  Text,
  TextVariant,
  getChainIconByChainId,
  useModal
} from '@mprolab/uikit'
import { Grid } from '@mui/material'
import { LoadingComponent } from 'components/LoadingComponent'
import { debounce, isEmpty, isNil, last } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowDownCircle, ArrowRightCircle, Info } from 'react-feather'
import * as Yup from 'yup'
import { FormikProvider, useFormik } from 'formik'
import { useGetContractsQuery } from 'state/services/config/config'
import styled, { useTheme } from 'styled-components'
import { BigNumber, ethers } from 'ethers'
import { useMproTokenContract } from 'hooks/contracts/useMproTokenContract'
import { useConnectPanel } from 'contexts/ConnectPanel'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'
import useToast from 'hooks/useToast'
import { useSetupNetwork } from 'hooks/useSetupNetwork'
import { useTranslation } from 'react-i18next'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import loading from 'react-useanimations/lib/loading'
import UseAnimations from 'react-useanimations'
import { useMproTokenContractAddress } from 'state/global/hooks'
import { ComingSoonPlaceholder } from 'components/ComingSoonPlaceholder'
import DepositModal from 'components/widgets/DepositModal/DepositModal'
import { ContractMenuItem } from './types'
import { EstimatedFee, SelectContractPopover } from './components'
import { MaxButton, TokenAmount } from '../General'

const Wrapper = styled(Flex)`
  gap: 8px;
  align-items: center;
`

const TokenWrapper = styled(Flex)`
  align-items: center;
  background: #171717;
  padding: 8px 16px 8px 8px;
  gap: 8px;
  border-radius: 45px;
`

const StyledSmallText = styled(Text)`
  font-size: 11px;
  line-height: normal;
  font-weight: 500;
`

const Bridge: React.FC = () => {
  const { t } = useTranslation()
  const { account, isActive, chainId } = useActiveWeb3React()
  const { colors } = useTheme()
  const isMobile = useThemeMediaQuery('mobile')
  const { data: contracts, isLoading } = useGetContractsQuery(undefined)
  const { toastError, toastSuccess, toastInfo } = useToast()
  const { setupNetwork } = useSetupNetwork()
  const { balance, estimateSendFee, sendFrom } = useMproTokenContract()
  const { setConnectPanelOpen } = useConnectPanel()
  const mproContractsList = contracts?.filter((contract) => contract.name === MprolabContractName.MPRO)
  /**
   * The chains the bridge may actually use. Deactivating one chain has to
   * narrow the bridge, not kill it: the previous logic disabled the whole
   * feature when ANY MPRO entry was inactive, so retiring a single deployment
   * would have shown every user "Coming soon" while the remaining chains were
   * perfectly able to trade.
   */
  const activeMproContracts = mproContractsList?.filter((contract) => contract.isActive !== false)
  const [tokenBalance, setTokenBalance] = useState(BigNumber.from(0))
  const [estimateFee, setEstimateFee] = useState(BigNumber.from(0))
  const [loadingEstimateFee, setLoadingEstimateFee] = useState(true)
  const [bridgeInProgress, setBridgeInProgress] = useState(false)
  const [inputResetKey, setInputResetKey] = useState(0)

  const supportedContract = activeMproContracts?.find((contract) => contract.chainId === chainId)
  const unsupportedNetworkLabel = {
    label: t('bridge.unsupportedNetwork'),
    chainId: ETH_CHAIN_ID_NUMBER
  }

  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      toAddress: account,
      amount: BigNumber.from(0),
      selectedFromContract: null as ContractMenuItem,
      selectedToContract: null as ContractMenuItem,
      customToAddress: false
    },
    validationSchema: Yup.object().shape({
      toAddress: Yup.string().when('customToAddress', {
        is: true,
        then: Yup.string()
          .required(t('bridge.requiredField'))
          .test('wallet-address', t('bridge.invalidAddress'), (walletAddress) => !!isAddress(walletAddress))
      }),
      amount: Yup.mixed<BigNumber>()
        .required('Required field')
        .test('max-balance', t('bridge.balanceExceedsMaxAllowed'), (value) => {
          if (!value) return false
          const formikBalance = BigNumber.from(value)
          return formikBalance.lte(tokenBalance)
        })
        .test('maxDecimals', t('bridge.maxDecimals'), (amount) => {
          const stringPrice = TokenBNtoString(chainId, amount)
          if (stringPrice.includes('.')) {
            return stringPrice.split('.')[1].length <= 6 && !stringPrice.includes('-')
          }
          return true
        })
    }),
    onSubmit: async (values, { validateForm }) => {
      await validateForm(values)
    }
  })

  const fromContractChainId = formik.values?.selectedFromContract?.chainId
  const toContractChainId = formik.values?.selectedToContract?.chainId

  const formattedContracts = (selectedContract?: ContractMenuItem): ContractMenuItem[] =>
    activeMproContracts?.map(({ chainId, address, name }) => ({
      label: getChainNameByChainId(chainId),
      icon: getChainIconByChainId(chainId),
      endIcon: <Checkbox readOnly singleSelect round checked={selectedContract?.chainId === chainId} />,
      address,
      chainId,
      tokenTicker: name
    }))

  const initialContractFrom = useMemo(() => {
    if (supportedContract) {
      return formattedContracts().find((contract) => contract.chainId === chainId)
    }
    return unsupportedNetworkLabel
  }, [chainId, activeMproContracts])

  const initialContractTo = last(
    formattedContracts()?.filter((contract) => contract.chainId !== initialContractFrom?.chainId)
  )

  const contractsFrom = useMemo(() => {
    return formattedContracts(formik.values.selectedFromContract)
  }, [activeMproContracts, formik.values.selectedFromContract])

  const contractsTo = useMemo(() => {
    const contract = formattedContracts(formik.values?.selectedToContract)?.filter(
      (singleContract) => singleContract.chainId !== fromContractChainId
    )
    return contract
  }, [activeMproContracts?.length, formik.values.selectedFromContract, formik.values.selectedToContract])

  const buttonTitle = () => {
    switch (true) {
      case !account || !isActive:
        return t('walletConnect.connectWallet')
      case chainId !== fromContractChainId:
        return t('walletConnect.switchNetwork')
      case bridgeInProgress:
        return (
          <Flex alignItems="center" gap="8px">
            <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.white3}>
              {t('continueInWallet')}
            </Text>
            <UseAnimations animation={loading} strokeColor={colors.white3} />
          </Flex>
        )
      default:
        return t('bridge.transfer')
    }
  }

  const [openDepositModalInfo] = useModal(<DepositModal network={getChainNameByChainId(toContractChainId)} />, false)
  const buttonHandler = async () => {
    switch (true) {
      case !account || !isActive:
        setConnectPanelOpen(true)
        break
      case chainId !== fromContractChainId:
        await setupNetwork({ chainId: fromContractChainId, modal: true, error: false })
        break
      default:
        try {
          if (formik.values.amount.isZero()) {
            formik.setFieldError('amount', t('bridge.amountRequired'))
            return
          }
          setBridgeInProgress(true)
          const bridgeTokensContract = await sendFrom({
            sourceChainId: fromContractChainId,
            destinationChainId: toContractChainId,
            toAddress: formik.values.toAddress,
            amount: formik.values.amount
          })
          toastInfo(t('bridge.inProgress'))
          openDepositModalInfo()
          await bridgeTokensContract.wait()
          setBridgeInProgress(false)
          toastSuccess(t('bridge.success'))
          formik.setFieldValue('amount', BigNumber.from(0))
        } catch (error: any) {
          /*
           * The decoder knows the two cases this switch used to handle and a
           * dozen it did not — a paused contract, no gas, a wallet that cannot
           * switch network, a transfer the token refused. Bridging is the
           * largest thing a holder does here after a swap, and "something went
           * wrong" is the one answer that helps nobody.
           */
          toastError(t('bridge.failed'), getErrorCodesDescription(t, error))
          setBridgeInProgress(false)
        }
    }
  }

  const handleSwapPlaces = async () => {
    if (!supportedContract) return
    await setupNetwork({
      chainId: toContractChainId,
      modal: true,
      error: false
    })
    // Swap the contracts
    const swapFromContract = formik.values.selectedFromContract

    const swapToContract = formik.values.selectedToContract

    // Set the new values
    formik.setFieldValue('selectedFromContract', swapToContract)
    formik.setFieldValue('selectedToContract', swapFromContract)
    setInputResetKey((prevKey) => prevKey + 1)
    formik.setFieldValue('amount', BigNumber.from(0))
  }

  const handleFromSelect = async (item: ContractMenuItem): Promise<void> => {
    await setupNetwork({
      chainId: item.chainId,
      modal: true,
      error: false
    })
    formik.setFieldValue('selectedFromContract', item)
    formik.setFieldValue('amount', BigNumber.from(0))
    setInputResetKey((prevKey) => prevKey + 1)

    if (toContractChainId === item.chainId) {
      const newToSelection = contractsTo.find((contract) => contract.chainId !== item.chainId)
      formik.setFieldValue('selectedToContract', newToSelection || null)
    }
  }

  const handleToSelect = (item: ContractMenuItem): void => {
    formik.setFieldValue('selectedToContract', item)
    formik.setFieldValue('amount', BigNumber.from(0))
    setInputResetKey((prevKey) => prevKey + 1)
  }

  const getEstimateFee = debounce(async () => {
    try {
      const fee = await estimateSendFee({
        sourceChainId: fromContractChainId,
        destinationChainId: toContractChainId,
        toAddress: formik.values.toAddress,
        amount: formik.values.amount
      })
      setEstimateFee(fee)
    } catch (error) {
      console.error('EstimateFee error', error)
    } finally {
      setLoadingEstimateFee(false)
    }
  }, 1500)

  const mproTokenContractAddresses = useMproTokenContractAddress()

  const getTokenBalance = useCallback(async () => {
    try {
      const tokenBalance = await balance({
        chainId: fromContractChainId
      })
      setTokenBalance(tokenBalance)
    } catch (error) {
      console.error('Token balance error', error)
    }
  }, [
    mproTokenContractAddresses,
    formik.values?.selectedFromContract?.chainId,
    formik.values?.selectedToContract?.chainId
  ])

  useEffect(() => {
    getTokenBalance()
  }, [
    formik.values?.selectedFromContract?.chainId,
    formik.values?.selectedToContract?.chainId,
    account,
    mproTokenContractAddresses,
    bridgeInProgress
  ])

  useEffect(() => {
    getEstimateFee()
    return () => getEstimateFee.cancel()
  }, [formik.values])

  useEffect(() => {
    formik.setFieldValue('toAddress', account)
  }, [account])

  useEffect(() => {
    if (activeMproContracts) {
      formik.setFieldValue('selectedFromContract', initialContractFrom)
      formik.setFieldValue('selectedToContract', initialContractTo)
    }
  }, [activeMproContracts?.length, chainId])

  useEffect(() => {
    if (!account) setTokenBalance(BigNumber.from(0))
  }, [account])

  // Input key need to have unique value (when changing from/to or make an bridge we need to update the input value key otherwise it will display previous input value)
  const inputKey = `${formik.values.selectedFromContract}-${formik.values.selectedToContract}-${bridgeInProgress}-${inputResetKey}`
  const receiveAmount = BNtoFormattedString(formik.values.amount, fromContractChainId)

  // Function to disable the bridge if there are no contracts or if any contract is disabled
  /**
   * A bridge needs two ends. Fewer than two active MPRO deployments is the
   * only state in which there is genuinely nothing to offer.
   */
  const disableBridge = () => (activeMproContracts?.length ?? 0) < 2

  const hasMoreThanSixDecimals = (value) => {
    const normalizedValue = value.replace(',', '.')
    const parts = normalizedValue.split('.')
    return parts.length > 1 && parts[1].length > 6
  }

  return (
    <>
      {disableBridge() && !isLoading ? (
        <Flex height="378px" justifyContent="center" alignItems="center">
          <ComingSoonPlaceholder />
        </Flex>
      ) : (
        <FormikProvider value={formik}>
          {isLoading ? (
            <LoadingComponent />
          ) : (
            <Flex gap="24px" fullWidth justifyContent="space-between" alignItems="center" flexDirection="column">
              <Grid container>
                <SelectContractPopover
                  title="From"
                  menuItems={contractsFrom}
                  onSelect={(item) => handleFromSelect(item as ContractMenuItem)}
                  contract={formik.values.selectedFromContract}
                />
                <Grid item xs={12} sm={2} md={2} display="flex" justifyContent="center" alignItems="center" mt="24px">
                  <Flex style={{ cursor: 'pointer' }}>
                    {isMobile ? (
                      <ArrowDownCircle color={colors.white} onClick={handleSwapPlaces} />
                    ) : (
                      <ArrowRightCircle color={colors.white} onClick={handleSwapPlaces} />
                    )}
                  </Flex>
                </Grid>
                <SelectContractPopover
                  title="To"
                  menuItems={contractsTo}
                  onSelect={(item) => handleToSelect(item as ContractMenuItem)}
                  contract={formik.values.selectedToContract}
                />
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TokenAmount
                    description="Amount"
                    refreshBalance={getTokenBalance}
                    {...{
                      tokenTicker: formik.values?.selectedFromContract?.tokenTicker,
                      tokenBalance: BNtoFormattedString(tokenBalance, stableCoinDecimals[fromContractChainId])
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Flex fullWidth flexDirection="column" gap="4px">
                    <Input
                      key={inputKey}
                      value={
                        formik.values.amount === BigNumber.from(0)
                          ? ' '
                          : TokenBNtoNumber(formik.values.amount, fromContractChainId).toString()
                      }
                      name="amount"
                      type="number"
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault()
                        }
                        blockInvalidFloat(e)
                      }}
                      error={!isNil(formik.errors.amount)}
                      errorMsg={formik.errors.amount as string}
                      onChange={(e) => {
                        let { value } = e.target
                        value = value.replace(',', '.')

                        if (!hasMoreThanSixDecimals(value)) {
                          const bigNumberValue = value
                            ? stringToBigNumber(value, stableCoinDecimals[fromContractChainId])
                            : BigNumber.from(0)
                          formik.setFieldValue('amount', bigNumberValue)
                        } else {
                          formik.setFieldError('amount', t('bridge.maxDecimals'))
                        }
                      }}
                      endIcon={
                        <Wrapper>
                          {tokenBalance.gt(BigNumber.from(0)) && (
                            <MaxButton
                              onClick={() => {
                                // Format the token amount to a string
                                let formattedTokenAmount = ethers.utils.formatUnits(tokenBalance, 18)
                                // Find the decimal point
                                const decimalIndex = formattedTokenAmount.indexOf('.')
                                // Trim to keep only 6 decimal places
                                if (decimalIndex !== -1) {
                                  // Cut the string to have a maximum of 6 decimal places
                                  formattedTokenAmount = formattedTokenAmount.substring(0, decimalIndex + 7)
                                }
                                // Convert the  string back to a BigNumber with 6 decimals
                                const truncatedMaxTokenAmount = ethers.utils.parseUnits(
                                  formattedTokenAmount,
                                  stableCoinDecimals[fromContractChainId]
                                )

                                formik.setFieldValue('amount', truncatedMaxTokenAmount)
                              }}
                            />
                          )}
                          <TokenWrapper>
                            <MPROToken />
                            <Text variant={TextVariant.BODY_SMALL_BOLD}>MPRO</Text>
                          </TokenWrapper>
                        </Wrapper>
                      }
                    />
                  </Flex>
                </Grid>
                <Grid item xs={12}>
                  <Flex
                    fullWidth
                    p="8px 16px"
                    borderRadius="8px"
                    justifyContent="space-between"
                    background={colors.gray10}
                  >
                    <Text variant={TextVariant.TEXT_XS_BOLD} color={colors.gray6}>
                      You receive
                    </Text>
                    <Text variant={TextVariant.TEXT_XS_BOLD}>
                      {isNil(formik.values?.selectedFromContract?.tokenTicker)
                        ? ''
                        : `${receiveAmount} ${formik.values?.selectedFromContract?.tokenTicker}`}
                    </Text>
                  </Flex>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Flex gap="8px" alignItems="center">
                    <Checkbox
                      singleSelect
                      onChange={() => {
                        if (formik.values.customToAddress) {
                          formik.setFieldValue('toAddress', account)
                        }
                        formik.setFieldValue('customToAddress', !formik.values.customToAddress)
                      }}
                    />
                    <Text variant={TextVariant.BODY_SMALL}>{t('bridge.differentWalletSend')}</Text>
                  </Flex>
                </Grid>
                {formik.values.customToAddress && (
                  <Grid item xs={12}>
                    <Flex gap="8px" flexDirection="column">
                      <Text variant={TextVariant.TEXT_XS_BOLD}>{t('bridge.destinationAddress')}</Text>
                      <Input
                        type="text"
                        placeholder="0x1234..."
                        onChange={(e) => {
                          const { value } = e.target
                          formik.setFieldValue('toAddress', value)
                        }}
                        value={formik.values.toAddress}
                        error={!isNil(formik.errors.toAddress)}
                        errorMsg={formik.errors.toAddress}
                        onBlur={formik.handleBlur}
                        maxLength={42}
                      />
                      <Flex
                        fullWidth
                        p="8px"
                        borderRadius="8px"
                        justifyContent="space-between"
                        background={colors.gray10}
                        gap="16px"
                      >
                        <Flex minWidth="24px" minHeight="24px" justifyContent="center" alignItems="center">
                          <Info color={colors.yellow} />
                        </Flex>
                        <Text color={colors.yellow} variant={TextVariant.BODY_SMALL}>
                          <b>{t('bridge.warning')}</b> {t('bridge.warningDescription')}
                        </Text>
                      </Flex>
                    </Flex>
                  </Grid>
                )}
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <EstimatedFee
                    fee={ethers.utils.formatEther(estimateFee.toString())}
                    ticker={getNativeCoinTickerByChainId(fromContractChainId)}
                    loading={loadingEstimateFee}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <Flex fullWidth>
                    <Button
                      type="submit"
                      fullWidth
                      disabled={bridgeInProgress || !formik.isValid}
                      onClick={buttonHandler}
                    >
                      {buttonTitle()}
                    </Button>
                  </Flex>
                </Grid>
                <Grid item xs={12}>
                  <Flex fullWidth alignItems="center" gap="8px" justifyContent="flex-end" mt="24px">
                    <StyledSmallText color={colors.gray6}>{t('bridge.poweredBy')}</StyledSmallText>
                    <LayerZeroLogo />
                  </Flex>
                </Grid>
              </Grid>
            </Flex>
          )}
        </FormikProvider>
      )}
    </>
  )
}

export default Bridge
